import React, { useMemo, useState } from 'react';
import { Select } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';

import TransactionCreativeTable from './TransactionCreativesTable';
import TransactionCreativeRingChart from './TransactionCreativeRingChart';
import EntityBreakdownCard from '../../../../../../../shared/designSystem/components/EntityBreakdownCard';
import { MenuItemText } from '../../../../../../../shared/globalStyling/styledText';
import { getRingColor } from '../../../../../../../shared/designSystem/components/BreakdownRingChart';

const { Option } = Select;

const SHOW_BY_OPTIONS = [
  { value: 'impressions', text: 'Impressions' },
  { value: 'totalTransactions', text: 'Transactions' },
];

// =============================================================================
// TransactionCreativeDetails — unified Creatives + Breakdown card (V2 tab).
//
// Same shape as Display / Publisher / V1-Transactions Creatives cards:
// one .campaign-overview__creatives section with the table in the body and
// a breakdown donut in the aside. No search in v1 — keeping that as-is.
// Loading state uses Skeleton in the full-card shell (parity with Creatives
// wrapper elsewhere).
// =============================================================================

const TransactionCreativeDetails = ({
  creatives = [],
  loading = false,
  byDateLoading = false,
  showMetrics = false,
}) => {
  const [creativeDimension, setCreativeDimension] = useState('impressions');

  // Sort by impressions desc, assign stable slice colors, attach the
  // `totalTransactions` alias the v1 ring chart used for the Transactions
  // breakdown dimension.
  const creativesWithColors = useMemo(
    () =>
      [...creatives]
        .sort((a, b) => (b.impressions || 0) - (a.impressions || 0))
        .map((creative, index) => ({
          ...creative,
          color: getRingColor(index),
          value: creative.name || creative.media || `creative-${index}`,
          totalTransactions: creative.transactions,
        })),
    [creatives]
  );

  return (
    <EntityBreakdownCard
      title={
        <>
          Creatives
          {byDateLoading && (
            <LoadingOutlined
              style={{ marginLeft: 8, fontSize: 14, color: '#94a3b8' }}
              spin
              aria-label="Loading by-date data"
            />
          )}
        </>
      }
      loading={loading}
      table={
        <TransactionCreativeTable creativeData={creativesWithColors} showMetrics={showMetrics} />
      }
      breakdownControl={
        <Select
          value={creativeDimension}
          onChange={(value) => setCreativeDimension(value)}
          getPopupContainer={(trigger) => trigger.parentNode}
          popupClassName="mjl-ds"
          className="campaign-overview__creatives-showby"
          size="small"
        >
          {SHOW_BY_OPTIONS.map((option) => (
            <Option key={option.value} value={option.value}>
              <MenuItemText text={option.text} />
            </Option>
          ))}
        </Select>
      }
      breakdownChart={
        <TransactionCreativeRingChart
          data={creativesWithColors}
          height={480}
          selectedCreativeDimension={creativeDimension}
        />
      }
    />
  );
};

export default TransactionCreativeDetails;
